import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiCopy, FiUserX, FiArrowLeft, FiUser, FiSliders, FiActivity, FiCloud, FiBarChart2 } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

import BASE_URL, { IMAGE_DOMAIN } from '../../../../config/config';
import Loader from '../../../../components/Loader';
import Popup from '../../../../components/Popup';
import CropperModal from '../../../../components/ui/CropperModal';
import useCropper from '../../../../components/ui/useCropper';

import useDetalleUsuario from './useDetalleUsuario';
import TarjetaIdentidad from './TarjetaIdentidad';
import KpisTickets from './KpisTickets';
import PanelActividad from './PanelActividad';
import Panel from './Panel';
import Pestanas from '../../../../components/ui/Pestanas';
import BarraGuardado from './BarraGuardado';
import FormularioInformacion from './FormularioInformacion';
import FormularioAjustes from './FormularioAjustes';
import ModalEliminarUsuario from './ModalEliminarUsuario';
import DriveExplorer from '../../../../components/drive/DriveExplorer';
import PanelIntegracionInforme from './PanelIntegracionInforme';
import { validarTelefonoInternacional } from '../../../../utils/telefono';
import { useQuery } from '@tanstack/react-query';
import { cabecerasAuth, claves } from '../../../../queries/cliente';
import '../../../../css/perfil-usuario.css';

const CAMPOS_DE_AJUSTES = ['role', 'estado', 'dpto_tecnico', 'emails_cc'];

const DetalleUsuario = () => {
  const { t } = useTranslation();
  const { usuario } = useParams();
  const navigate = useNavigate();
  // Baja de la cuenta: el listado de técnicos solo hace falta al abrir el modal
  const [bajaAbierta, setBajaAbierta] = useState(false);
  const consultaTecnicos = useQuery({
    queryKey: claves.tecnicos(),
    queryFn: async () => (await axios.get(`${BASE_URL}/usuario/tecnicos`, cabecerasAuth())).data.usuarios || [],
    enabled: bajaAbierta,
    staleTime: 5 * 60_000,
  });

  const textoContrasena = (usuarioData, contrasena) => t('perfilUsuario.textoContrasenaCorreo', {
    nombre: usuarioData.nombre, usuario: usuarioData.usuario, contrasena,
  });

  const {
    token, esAdmin, viewerRole,
    usuarioData, form, actualizarCampo, cargando, error,
    tickets, cargandoTickets,
    auditoria, cargandoAuditoria,
    catalogoServicios, serviciosUsuario, serviciosAsignados, alternarServicio, cargandoServicios,
    camposCambiados, serviciosCambiados, totalCambios, hayCambios,
    descartarCambios, guardar, cambiarEstado,
  } = useDetalleUsuario(usuario);

  // Los técnicos también entran en "Ajustes avanzados", pero allí solo ven
  // los correos en copia: permisos, servicios y seguridad siguen siendo de admin.
  const esStaff = esAdmin || viewerRole === 'tecnico';

  const [tabActiva, setTabActiva] = useState('informacion');
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [cambiandoEstado, setCambiandoEstado] = useState(false);

  const [confirmacion, setConfirmacion] = useState(null);
  const [salidaPendiente, setSalidaPendiente] = useState(null);

  const [reseteando, setReseteando] = useState(false);
  const [showResetPopup, setShowResetPopup] = useState(false);
  const [resetTempPassword, setResetTempPassword] = useState('');

  const [fotoPreview, setFotoPreview] = useState(null);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const fileInputRef = useRef(null);

  const { rawImageSrc, showCropper, crop, setCrop, zoom, setZoom,
    onCropComplete, openCropper, cancelCrop, applyCrop } = useCropper({
    onCropDone: async ({ blob, previewUrl }) => {
      setSubiendoFoto(true);
      try {
        const fd = new FormData();
        fd.append('fotoPerfil', blob, 'avatar.webp');
        const resp = await axios.put(`${BASE_URL}/usuario/actualizar-imagen/${usuario}`, fd, {
          headers: { Authorization: token, 'Content-Type': 'multipart/form-data' },
        });
        if (resp.data.success) {
          setFotoPreview(previewUrl);
          toast.success(t('comun.fotoActualizada'));
        } else {
          toast.error(resp.data.message || t('perfilUsuario.errorImagen'));
        }
      } catch (err) {
        console.error('Error al actualizar la imagen:', err);
        toast.error(t('perfilUsuario.errorImagen'));
      } finally {
        setSubiendoFoto(false);
      }
    },
  });

  // Aviso del navegador al cerrar la pestaña con cambios sin guardar
  useEffect(() => {
    if (!hayCambios) return undefined;
    const alSalir = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', alSalir);
    return () => window.removeEventListener('beforeunload', alSalir);
  }, [hayCambios]);

  const copiar = async (texto, mensaje) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success(mensaje);
    } catch (err) {
      toast.error(t('perfilUsuario.errorCopiar'));
    }
  };

  const validarFormulario = () => {
    const errs = {};

    if (!form.nombre || form.nombre.trim() === '') errs.nombre = t('perfilUsuario.errorNombreVacio');
    if (!form.apellidos || form.apellidos.trim() === '') errs.apellidos = t('perfilUsuario.errorApellidosVacio');
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) errs.email = t('perfilUsuario.errorEmailInvalido');

    if (form.telefono && form.telefono.trim() !== '') {
      const telefono = validarTelefonoInternacional(form.telefono);
      if (!telefono.valido) errs.telefono = telefono.error === 'formato'
        ? t('perfilUsuario.errorTelefonoFormato')
        : t('perfilUsuario.errorTelefonoInvalido');
    }

    return errs;
  };

  const ejecutarGuardado = async () => {
    setGuardando(true);
    try {
      const telefono = validarTelefonoInternacional(form.telefono);
      await guardar({ telefono: telefono.valido ? telefono.numero : form.telefono });
      toast.success(t('perfilUsuario.datosActualizados'));
    } catch (err) {
      console.error('Error al actualizar:', err);
      toast.error(err.response?.data?.message || t('perfilUsuario.errorGuardarCambios'));
    } finally {
      setGuardando(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (guardando) return;

    const errs = validarFormulario();
    if (Object.keys(errs).length > 0) {
      setErrores(errs);
      // Los errores viven en la pestaña de información: llevamos al usuario allí
      setTabActiva('informacion');
      toast.error(t('perfilUsuario.revisaCampos'));
      return;
    }
    setErrores({});

    const pasaAAdmin = form.role === 'admin' && usuarioData.role !== 'admin';
    const pasaASuspendido = form.estado === 'suspendido' && usuarioData.estado !== 'suspendido';

    if (pasaAAdmin || pasaASuspendido) {
      const mensajes = [];
      if (pasaAAdmin) mensajes.push(t('perfilUsuario.avisoAdmin'));
      if (pasaASuspendido) mensajes.push(t('perfilUsuario.avisoSuspender'));
      setConfirmacion({
        titulo: t('perfilUsuario.confirmarCambio'),
        mensaje: mensajes.join(' '),
        onConfirmar: ejecutarGuardado,
      });
      return;
    }

    await ejecutarGuardado();
  };

  const aplicarCambioEstado = async (nuevoEstado) => {
    setCambiandoEstado(true);
    try {
      await cambiarEstado(nuevoEstado);
      toast.success(nuevoEstado === 'suspendido' ? t('perfilUsuario.cuentaSuspendida') : t('perfilUsuario.cuentaVerificada'));
    } catch (err) {
      console.error('Error al cambiar el estado:', err);
      toast.error(t('perfilUsuario.errorCambiarEstado'));
    } finally {
      setCambiandoEstado(false);
    }
  };

  const pedirCambioEstado = (nuevoEstado) => {
    if (nuevoEstado === 'suspendido') {
      setConfirmacion({
        titulo: t('perfilUsuario.suspenderCuenta'),
        mensaje: t('perfilUsuario.avisoPerderAcceso'),
        onConfirmar: () => aplicarCambioEstado('suspendido'),
      });
      return;
    }
    aplicarCambioEstado(nuevoEstado);
  };

  // Cualquier salida (volver, ir al panel de tickets) pasa por aquí para no
  // perder cambios sin querer.
  const intentarSalir = (accion) => {
    if (!hayCambios) {
      accion();
      return;
    }
    setSalidaPendiente(() => accion);
  };

  const pedirResetearContrasena = () => {
    setConfirmacion({
      titulo: t('perfilUsuario.resetearContrasena'),
      mensaje: t('perfilUsuario.avisoResetearContrasena'),
      textoConfirmar: t('comun.continuar'),
      onConfirmar: () => resetearContrasena(),
    });
  };

  const resetearContrasena = async () => {
    setReseteando(true);
    try {
      const resp = await axios.put(`${BASE_URL}/usuario/reset-password/${usuario}`, {}, {
        headers: { Authorization: token },
      });
      if (resp.data.success) {
        setResetTempPassword(resp.data.tempPassword);
        setShowResetPopup(true);
      } else {
        toast.error(resp.data.message || t('perfilUsuario.errorResetPassword'));
      }
    } catch (err) {
      console.error('Error al resetear la contraseña:', err);
      toast.error(t('perfilUsuario.errorResetPassword'));
    } finally {
      setReseteando(false);
    }
  };

  // El panel filtra por `creador` (el login), no por texto libre: buscar el
  // nombre traía tickets de otras cuentas y el usuario de acceso no se
  // comparaba en la búsqueda. Se limpian los filtros previos para que el
  // listado que se abre sean exactamente los tickets de esta ficha.
  const irAVerTodosLosTickets = () => {
    try {
      localStorage.setItem('ticketManagerFiltros', JSON.stringify({ creador: usuario }));
    } catch (err) {
      // ignore
    }
    navigate('/dashboard/tickets');
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) openCropper(file);
    e.target.value = '';
  };

  // Los chips de la tarjeta muestran la selección pendiente del admin, no la
  // guardada: así la tarjeta y el asignador cuentan lo mismo.
  const serviciosVisibles = useMemo(() => {
    if (!esAdmin) return serviciosUsuario;
    const porId = new Map([...serviciosUsuario, ...catalogoServicios].map((s) => [s.id, s]));
    return serviciosAsignados.map((id) => porId.get(id)).filter(Boolean);
  }, [esAdmin, serviciosUsuario, catalogoServicios, serviciosAsignados]);

  const pestanas = useMemo(() => {
    const cambiosDeAjustes = camposCambiados.filter((c) => CAMPOS_DE_AJUSTES.includes(c)).length;
    const lista = [
      {
        id: 'informacion',
        label: t('perfilUsuario.informacion'),
        icono: FiUser,
        pendientes: camposCambiados.length - cambiosDeAjustes,
      },
    ];
    if (esStaff) {
      lista.push({
        id: 'ajustes',
        label: t('perfilUsuario.ajustesAvanzados'),
        icono: FiSliders,
        pendientes: cambiosDeAjustes + (serviciosCambiados ? 1 : 0),
      });
      lista.push({ id: 'drive', label: t('drive.titulo'), icono: FiCloud, pendientes: 0 });
    }
    lista.push({ id: 'actividad', label: t('perfilUsuario.actividad'), icono: FiActivity, pendientes: 0 });
    // Conexión con el informe web de Adhoc Manager: solo cuentas de cliente y solo admin
    if (esAdmin && usuarioData?.role === 'user') {
      lista.push({ id: 'informe', label: t('integracionInforme.pestana'), icono: FiBarChart2, pendientes: 0 });
    }
    return lista;
  }, [camposCambiados, serviciosCambiados, esStaff, esAdmin, usuarioData?.role, t]);

  if (cargando) return <div className="loader-fullscreen"><Loader /></div>;

  if (error || !usuarioData) {
    return (
      <main className="site-width pu-page">
        <div className="pu-vacio pu-vacio--pagina">
          <FiUserX />
          <p>{error || t('perfilUsuario.usuarioNoEncontrado')}</p>
          <button className="pu-btn-primario" onClick={() => navigate('/dashboard/usuarios')}>
            {t('perfilUsuario.volverAlListado')}
          </button>
        </div>
      </main>
    );
  }

  const fotoActual = fotoPreview
    || (usuarioData.fotoPerfil
      ? (usuarioData.fotoPerfil.startsWith('http') ? usuarioData.fotoPerfil : `${IMAGE_DOMAIN}${usuarioData.fotoPerfil}`)
      : `${IMAGE_DOMAIN}/images/default-user.png`);

  return (
    <main className="site-width pu-page">
      <ToastContainer position="bottom-right" autoClose={3000} />

      {bajaAbierta && usuarioData && (
        <ModalEliminarUsuario
          abierto={bajaAbierta}
          usuario={usuario}
          rol={usuarioData.role}
          tecnicos={consultaTecnicos.data || []}
          onCerrar={() => setBajaAbierta(false)}
          onHecho={(resultado) => {
            setBajaAbierta(false);
            toast.success(resultado.modo === 'eliminar'
              ? t('bajaUsuario.hechoEliminar', { usuario })
              : t('bajaUsuario.hechoArchivar', { usuario }));
            // Eliminada ya no hay ficha que mirar; archivada, se recarga
            if (resultado.modo === 'eliminar') navigate('/dashboard/usuarios');
            else window.location.reload();
          }}
        />
      )}

      <div className="pu-topbar">
        <button
          type="button"
          className="pu-volver"
          onClick={() => intentarSalir(() => navigate('/dashboard/usuarios'))}
        >
          <FiArrowLeft /> {t('menu.usuarios')}
        </button>
        <div className="pu-topbar-titulo">
          <span>{t('perfilUsuario.directorioUsuarios')}</span>
          <h2>{t('perfilUsuario.perfilCuenta')}</h2>
          <p className="pu-topbar-nota">{t('perfilUsuario.gestionaIdentidad')}</p>
        </div>
      </div>

      <div className="pu-layout">
        <aside className="pu-columna pu-lateral">
          <TarjetaIdentidad
            usuarioData={usuarioData}
            fotoActual={fotoActual}
            subiendoFoto={subiendoFoto}
            onCambiarFoto={() => fileInputRef.current?.click()}
            puedeCambiarEstado={esAdmin}
            cambiandoEstado={cambiandoEstado}
            onCambiarEstado={pedirCambioEstado}
            onCopiar={copiar}
            servicios={serviciosVisibles}
            cargandoServicios={cargandoServicios}
            serviciosPendientes={serviciosCambiados}
          />
        </aside>

        <section className="pu-columna">
          <KpisTickets tickets={tickets} cargando={cargandoTickets} />

          <Pestanas
            pestanas={pestanas}
            activa={tabActiva}
            onCambiar={setTabActiva}
          />

          <div
            role="tabpanel"
            id={`panel-${tabActiva}`}
            aria-labelledby={`tab-${tabActiva}`}
          >
            {tabActiva === 'drive' ? (
              <DriveExplorer propietario={usuario} canManage compact />
            ) : tabActiva === 'informe' ? (
              <PanelIntegracionInforme
                usuario={usuario}
                onAviso={(tipo, mensaje) => (tipo === 'error' ? toast.error(mensaje) : toast.success(mensaje))}
              />
            ) : tabActiva === 'actividad' ? (
              <PanelActividad
                tickets={tickets}
                cargandoTickets={cargandoTickets}
                onVerTodos={() => intentarSalir(irAVerTodosLosTickets)}
                auditoria={auditoria}
                cargandoAuditoria={cargandoAuditoria}
                mostrarAuditoria={esAdmin}
              />
            ) : (
              <form className="pu-form" onSubmit={handleSubmit}>
                <Panel>
                  {tabActiva === 'informacion' && (
                    <FormularioInformacion
                      usuarioData={usuarioData}
                      form={form}
                      errores={errores}
                      onCampo={actualizarCampo}
                    />
                  )}

                  {tabActiva === 'ajustes' && esStaff && (
                    <FormularioAjustes
                      esAdmin={esAdmin}
                      form={form}
                      onCampo={actualizarCampo}
                      catalogoServicios={catalogoServicios}
                      serviciosAsignados={serviciosAsignados}
                      onAlternarServicio={alternarServicio}
                      cargandoServicios={cargandoServicios}
                      reseteando={reseteando}
                      onResetearContrasena={pedirResetearContrasena}
                      puedeDarDeBaja={esAdmin}
                      onDarDeBaja={() => setBajaAbierta(true)}
                    />
                  )}
                </Panel>

                <BarraGuardado
                  visible={hayCambios}
                  totalCambios={totalCambios}
                  guardando={guardando}
                  onDescartar={() => { descartarCambios(); setErrores({}); }}
                />
              </form>
            )}
          </div>
        </section>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {showCropper && (
        <CropperModal
          rawImageSrc={rawImageSrc}
          crop={crop} setCrop={setCrop}
          zoom={zoom} setZoom={setZoom}
          onCropComplete={onCropComplete}
          onCancel={cancelCrop}
          onApply={applyCrop}
        />
      )}

      {/* Confirmación de acciones sensibles (rol admin, suspensión, reseteo
          de contraseña): un único diálogo reutilizado por todas ellas. */}
      <Popup
        open={Boolean(confirmacion)}
        title={confirmacion?.titulo || t('perfilUsuario.confirmarCambio')}
        onClose={() => setConfirmacion(null)}
        showConfirm={false}
        cancelText={t('comun.cancelar')}
        footerActions={(
          <button
            className="boton-aceptar-popup"
            onClick={() => {
              const accion = confirmacion?.onConfirmar;
              setConfirmacion(null);
              if (accion) accion();
            }}
          >
            {confirmacion?.textoConfirmar || t('comun.confirmar')}
          </button>
        )}
      >
        <p>{confirmacion?.mensaje}</p>
        <p>{t('perfilUsuario.quieresContinuar')}</p>
      </Popup>

      {/* Salir con cambios sin guardar */}
      <Popup
        open={Boolean(salidaPendiente)}
        title={t('perfilUsuario.cambiosSinGuardarTitulo')}
        onClose={() => setSalidaPendiente(null)}
        showConfirm={false}
        footerActions={(
          <button
            className="boton-aceptar-popup"
            onClick={() => {
              const accion = salidaPendiente;
              setSalidaPendiente(null);
              descartarCambios();
              if (accion) accion();
            }}
          >
            {t('perfilUsuario.salirSinGuardar')}
          </button>
        )}
      >
        <p>{t('perfilUsuario.salirPerderCambios', { count: totalCambios })}</p>
      </Popup>

      {/* Resultado del reseteo de contraseña */}
      <Popup
        open={showResetPopup}
        title={t('perfilUsuario.contrasenaReseteada')}
        onClose={() => setShowResetPopup(false)}
        showConfirm={false}
        footerActions={(
          <button
            className="boton-aceptar-popup"
            onClick={() => copiar(textoContrasena(usuarioData, resetTempPassword), t('perfilUsuario.textoCopiadoPortapapeles'))}
          >
            {t('perfilUsuario.copiarTextoCompleto')}
          </button>
        )}
      >
        <div className="form-crear-usuario">
          <div className="form-field">
            <label>{t('perfilUsuario.nuevaContrasenaTemporal')}</label>
            <div className="pu-reset-fila">
              <p className="pu-reset-valor">{resetTempPassword}</p>
              <button
                type="button"
                className="pu-btn-secundario"
                onClick={() => copiar(resetTempPassword, t('perfilUsuario.contrasenaCopiada'))}
              >
                <FiCopy /> {t('comun.copiar')}
              </button>
            </div>
          </div>
          <div className="form-field">
            <label>{t('perfilUsuario.textoParaEnviar')}</label>
            <textarea readOnly rows={8} value={textoContrasena(usuarioData, resetTempPassword)} />
          </div>
        </div>
      </Popup>
    </main>
  );
};

export default DetalleUsuario;
